import { FlexGrid, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { Button } from '@mui/material';
import { admin } from 'apis';
import { useQuery } from 'react-query';
import { useRef, useState, useEffect } from 'react';

const MAX_LENGTH = 1000;

const AppSettings = () => {
  const settingsGridRef = useRef();
  const appSettingsGridRef = useRef();
  const [settingsChanges, setSettingsChanges] = useState({});
  const [appSettingsChanges, setAppSettingsChanges] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [originalAppSettings, setOriginalAppSettings] = useState({});
  const [isSettingsChanged, setIsSettingsChanged] = useState(false);
  const [isAppSettingsChanged, setIsAppSettingsChanged] = useState(false);
  const [appSettings, setAppSettings] = useState([]);
  const [settings, setSettings] = useState([]);


  const dataQuery = useQuery(['appSettings'], async () => {
    const result = await admin.getAppSettings();
    return result;
  });

  useEffect(() => {
    if (dataQuery.status === 'success' && dataQuery.data?.appSettings) {
      setAppSettings(dataQuery.data.appSettings);
      setSettings(dataQuery.data.settings);
    }
  }, [dataQuery.status, dataQuery.data]);

  /*################## AppSettings Table ##########*/

  const handleAddAppSetting = () => {
    const newItem = {
      ID: null,
      DataKey: '',
      DataValue: '',
      Comments: '',
      Created: null,
      Updated: null,
      IsNew: true,
      IsDeleted: false
    };
    setAppSettings((prev) => [...prev, newItem]);
    setIsAppSettingsChanged(true);
  };

  const handleDeleteSelectedAppSetting = () => {
    const grid = appSettingsGridRef.current;
    if (!grid) return;

    const selected = grid.collectionView.currentItem;
    if (!selected) return;

    selected.IsDeleted = true;
    grid.collectionView.refresh();
    setIsAppSettingsChanged(true);
  };

  const handleSaveAppSettings = async () => {
    try {
      const sanitize = (val, fallback = '') => {
        if (typeof val === 'string'){
          if (val.startsWith('data:image/png;base64')){
            return val.trim();
          }
          return val.trim().substring(0, MAX_LENGTH)
        }
        return fallback;
        }
      // typeof val === 'string' ? val.trim().substring(0, MAX_LENGTH) : fallback;
  
      const fullList = appSettings.map((item) => ({
        ID: item.ID ?? item.id ?? null,
        DataKey: sanitize(item.DataKey || item.dataKey),
        DataValue: sanitize(item.DataValue || item.dataValue),
        Comments: sanitize(item.Comments || item.comments, ''),
        Created: item.Created ?? item.created ?? null,
        Updated: item.Updated ?? item.updated ?? null,
        IsNew: item.IsNew ?? item.isNew ?? (item.ID == null && item.id == null),
        IsDeleted: item.IsDeleted ?? item.isDeleted ?? false
      }));
  
      await admin.postAppSettings(fullList);
      await dataQuery.refetch();
  
      setAppSettingsChanges({});
      setOriginalAppSettings({});
      setIsAppSettingsChanged(false);
    } catch (err) {
      console.error('Failed to save app settings:', err);
      alert('Failed to save changes. Please try again.');
    }
  };

  /*#############################################
  ############## AppSettings Table ##############
  #############################################*/

  const handleAddSetting = () => {
    const newItem = {
      ID: null,
      DataKey: '',
      DataValue: '',
      IsNew: true,
      IsDeleted: false
    };
    setSettings((prev) => [...prev, newItem]);
    setIsSettingsChanged(true);
  };

  const handleDeleteSelectedSetting = () => {
    const grid = settingsGridRef.current;
    if (!grid) return;
  
    const selected = grid.collectionView.currentItem;
    if (!selected) return;
  
    selected.IsDeleted = true;
    grid.collectionView.refresh();
    setIsSettingsChanged(true);
  };
  
  const handleSaveSettings = async () => {
    try {
      const sanitize = (val, fallback = '') => {
        if (typeof val === 'string'){
          if (val.startsWith('data:image/png;base64')){
            return val.trim();
          }
          return val.trim().substring(0, MAX_LENGTH)
        }
        return fallback;
      }
        // typeof val === 'string' ? val.trim().substring(0, MAX_LENGTH) : fallback;
  
      const fullList = settings.map((item) => ({
        ID: item.ID ?? item.id ?? null,
        DataKey: sanitize(item.DataKey || item.dataKey),
        DataValue: sanitize(item.DataValue || item.dataValue),
        IsNew: item.IsNew ?? item.isNew ?? (item.ID == null && item.id == null),
        IsDeleted: item.IsDeleted ?? item.isDeleted ?? false
      }));
  
      await admin.postSettings(fullList);
      await dataQuery.refetch();
  
      setSettingsChanges({});
      setOriginalSettings({});
      setIsSettingsChanged(false);
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save settings. Please try again.');
    }
  };
  
  

  const trackChanges = (gridRef, setChanges, setChanged, originalData) => {
    const grid = gridRef.current;

    grid.cellEditEnding.addHandler((s, e) => {
      const editedItem = s.rows[e.row].dataItem;
      const editedField = s.columns[e.col].binding;
      const editedValue = s.activeEditor.value;
      const originalValue = originalData[editedItem.ID ?? editedItem.id]?.[editedField];

      if (editedValue !== originalValue || editedItem[editedField] !== originalValue) {
        setChanges((prevChanges) => {
          const updatedChanges = { ...prevChanges };
          updatedChanges[editedItem.ID ?? editedItem.id] = {
            ...editedItem,
            [editedField]: editedValue
          };
          return updatedChanges;
        });
        setChanged(true);
      }
    });
  };

  const initializeSettings = (flex) => {
    settingsGridRef.current = flex;
    flex.collectionView.trackChanges = true;
    const originalData = {};
    flex.collectionView.itemsEdited.forEach((item) => {
      originalData[item.ID ?? item.id] = { ...item };
    });
    setOriginalSettings(originalData);
    trackChanges(settingsGridRef, setSettingsChanges, setIsSettingsChanged, originalData);
  };

  const initializeAppSettings = (flex) => {
    appSettingsGridRef.current = flex;
    flex.collectionView.trackChanges = true;
    const originalData = {};
    flex.collectionView.itemsEdited.forEach((item) => {
      originalData[item.ID ?? item.id] = { ...item };
    });
    setOriginalAppSettings(originalData);
    trackChanges(appSettingsGridRef, setAppSettingsChanges, setIsAppSettingsChanged, originalData);
  };

  return {
    loading: <p>loading</p>,
    success: (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '20px', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>Settings</h1>
            <div>
            <Button variant="outlined" color="primary" onClick={handleAddSetting} style={{ marginRight: '10px' }}>
                ➕ Add
            </Button>
            <Button variant="outlined" color="error" onClick={handleDeleteSelectedSetting}>
                🗑️ Delete
            </Button>
            </div>
        </div>
        <FlexGrid
            initialized={(flex) => initializeSettings(flex)}
            className="explorer-grid"
            itemsSource={settings.filter((item) => !item.IsDeleted)}
            autoSizeMode={1}
            headersVisibility={3}
            alternatingRowStep={0}
          >
            <FlexGridColumn header="ID" binding="id" width="*" />
            <FlexGridColumn header="DataKey" binding="dataKey" width="*" />
            <FlexGridColumn header="DataValue" binding="dataValue" width="*" />
         </FlexGrid> 
        
        {isSettingsChanged && (
            <Button variant="contained" color="primary" onClick={handleSaveSettings}>
            💾 Save Settings
            </Button>
        )}
        </div>


        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '20px', gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>App Settings</h1>
            <div>
              <Button variant="outlined" color="primary" onClick={handleAddAppSetting} style={{ marginRight: '10px' }}>
                ➕ Add
              </Button>
              <Button variant="outlined" color="error" onClick={handleDeleteSelectedAppSetting}>
                🗑️ Delete
              </Button>
            </div>
          </div>
          <FlexGrid
            initialized={(flex) => initializeAppSettings(flex)}
            className="explorer-grid"
            itemsSource={appSettings.filter((item) => !item.IsDeleted)}
            autoSizeMode={1}
            headersVisibility={3}
            alternatingRowStep={0}
          >
            <FlexGridColumn header="ID" binding="id" width="*" />
            <FlexGridColumn header="DataKey" binding="dataKey" width="*" />
            <FlexGridColumn header="DataValue" binding="dataValue" width="*" />
            <FlexGridColumn header="Comments" binding="comments" width="*" />
            <FlexGridColumn header="Created" binding="created" width="*" />
            <FlexGridColumn header="Updated" binding="updated" width="*" />
          </FlexGrid>
          {isAppSettingsChanged && (
            <Button variant="contained" color="primary" onClick={handleSaveAppSettings}>
              💾 Save App Settings
            </Button>
          )}
        </div>
      </>
    ),
    error: <p>error</p>
  }[dataQuery.status];
};

export default AppSettings;
